/**
 * Prevents infinite rule loops during a single automation evaluation pass.
 *
 * Strategy:
 * - Track which rules are currently executing (stack-based)
 * - If a rule's action would re-enter a rule already on the stack → halt
 * - Track which fields were written by which rules for field-level cycle detection
 * - Maximum 1 cascading pass (A fires B, but B cannot fire A or itself)
 */

export class AutomationCycleDetector {
  private executionStack: Set<string> = new Set();
  private fieldWriteLog: Map<string, string> = new Map(); // field → ruleId
  private warnings: string[] = [];

  /** Returns false if ruleId is already on the execution stack */
  canExecute(ruleId: string): boolean {
    if (this.executionStack.has(ruleId)) {
      this.warnings.push(
        `Cycle detected: rule "${ruleId}" is already executing — halting to prevent infinite loop`,
      );
      return false;
    }
    return true;
  }

  startExecution(ruleId: string): void {
    this.executionStack.add(ruleId);
  }

  endExecution(ruleId: string): void {
    this.executionStack.delete(ruleId);
  }

  recordFieldWrite(field: string, ruleId: string): void {
    this.fieldWriteLog.set(field, ruleId);
  }

  /**
   * Checks if executing this rule would cause a cycle based on field writes.
   * If the rule's trigger field was just written by another rule that was itself
   * triggered by the current rule, it's a cycle.
   */
  wouldCauseCycle(ruleId: string, triggerField?: string): boolean {
    if (!triggerField) return false;

    const writerRuleId = this.fieldWriteLog.get(triggerField);
    if (!writerRuleId) return false;

    // If the field was written by a different rule that's on the stack, it's a cascade
    if (writerRuleId !== ruleId && this.executionStack.has(writerRuleId)) {
      this.warnings.push(
        `Cascade cycle detected: rule "${ruleId}" triggered by field "${triggerField}" ` +
        `written by rule "${writerRuleId}" which is still executing`,
      );
      return true;
    }

    // Self-referencing: rule wrote a field that would trigger itself
    if (writerRuleId === ruleId) {
      this.warnings.push(
        `Self-reference detected: rule "${ruleId}" would re-trigger itself via field "${triggerField}"`,
      );
      return true;
    }

    return false;
  }

  reset(): void {
    this.executionStack.clear();
    this.fieldWriteLog.clear();
    this.warnings = [];
  }

  getWarnings(): string[] {
    return [...this.warnings];
  }
}
